import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { BRAND_LOGO_LOCKUP } from "../lib/data";
import { scrollToSeaMossChoice } from "../lib/plainGoldCart";
import { HOME_CTA_DARK_PRIMARY, HOME_CTA_PRIMARY } from "../lib/homeCta";
import { useShellTheme } from "./layout/ShellTheme";

const LINKS = [
  { to: "/shop", label: "Shop", testId: "nav-shop" },
  { to: "/reviews", label: "Reviews", testId: "nav-reviews" },
  { to: "/community-reward", label: "Reward", testId: "nav-reward" },
  { to: "/learn", label: "Learn", testId: "nav-learn" },
];

const Navbar = () => {
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const dark = useShellTheme() === "dark";
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const goToOrder = () => {
    closeMenu();
    if (location.pathname === "/") {
      scrollToSeaMossChoice();
      return;
    }
    navigate("/#choose-sea-moss");
  };

  const navClass = ({ isActive }) =>
    dark
      ? `whitespace-nowrap transition hover:text-gold py-3.5 md:py-0 min-h-[44px] md:min-h-0 flex items-center ${
          isActive ? "text-ivory font-medium" : "text-ivory/70"
        }`
      : `whitespace-nowrap transition hover:text-forest py-3.5 md:py-0 min-h-[44px] md:min-h-0 flex items-center ${
          isActive ? "text-forest font-medium" : "text-botanical"
        }`;

  const mobileRowClass = dark
    ? "py-3.5 text-lg text-ivory border-b border-white/10"
    : "py-3.5 text-lg text-forest border-b border-cream/80";

  return (
    <header
      className={
        dark
          ? "sticky top-0 z-50 backdrop-blur-xl bg-void/80 border-b border-white/10"
          : "sticky top-0 z-50 backdrop-blur-xl bg-offwhite/90 border-b border-cream"
      }
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-20 md:h-24 flex items-center justify-between gap-3">
        <Link
          to="/"
          className={`flex items-center justify-center shrink-0 z-[60] rounded-full overflow-hidden select-none ${
            dark ? "bg-ivory ring-1 ring-white/20" : "bg-white ring-1 ring-cream/90 shadow-sm"
          }`}
          data-testid="brand-logo"
          aria-label="Moss Matrix home"
        >
          <img
            src={BRAND_LOGO_LOCKUP}
            alt="Moss Matrix"
            className="h-[48px] w-[48px] md:h-[60px] md:w-[60px] object-contain p-1"
            draggable={false}
          />
        </Link>

        <nav
          className="hidden md:flex items-center flex-wrap justify-center gap-x-4 lg:gap-x-7 text-[13px] lg:text-sm flex-1 min-w-0 px-2"
          aria-label="Primary"
        >
          {LINKS.map((item) => (
            <NavLink key={item.to} to={item.to} end className={navClass} data-testid={item.testId}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            onClick={() => setOpen(true)}
            data-testid="open-cart-btn"
            className={
              dark
                ? "relative inline-flex items-center gap-2 rounded-full border border-ivory/20 bg-white/[0.04] px-3 sm:px-4 py-2.5 min-h-[44px] min-w-[44px] sm:min-w-0 text-ivory hover:bg-white/[0.1] hover:border-ivory/40 transition-all"
                : "relative inline-flex items-center gap-2 rounded-full border border-forest/15 px-3 sm:px-4 py-2.5 min-h-[44px] min-w-[44px] sm:min-w-0 hover:bg-forest hover:text-cream transition-all"
            }
            aria-label="Open basket"
          >
            <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm tracking-wide hidden sm:inline">Cart</span>
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-gold text-void text-[10px] font-semibold rounded-full w-5 h-5 grid place-items-center"
              >
                {count}
              </span>
            )}
          </button>

          <button
            type="button"
            className={
              dark
                ? "md:hidden inline-flex items-center justify-center rounded-full border border-ivory/20 p-2.5 min-h-[44px] min-w-[44px] text-ivory hover:bg-white/[0.08]"
                : "md:hidden inline-flex items-center justify-center rounded-full border border-forest/15 p-2.5 min-h-[44px] min-w-[44px] text-forest hover:bg-cream"
            }
            aria-expanded={menuOpen}
            aria-controls="mobile-primary-nav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X className="w-5 h-5" strokeWidth={1.6} /> : <Menu className="w-5 h-5" strokeWidth={1.6} />}
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div
          id="mobile-primary-nav"
          className={
            dark
              ? "md:hidden fixed inset-x-0 top-20 bottom-0 z-40 bg-void border-t border-white/10 overflow-y-auto px-5 py-6 flex flex-col"
              : "md:hidden fixed inset-x-0 top-20 bottom-0 z-40 bg-offwhite border-t border-cream overflow-y-auto px-5 py-6 flex flex-col"
          }
        >
          <button
            type="button"
            onClick={goToOrder}
            className={`${dark ? HOME_CTA_DARK_PRIMARY : HOME_CTA_PRIMARY} w-full min-h-[52px] px-6 py-3.5 text-sm mb-6`}
          >
            Choose your sea moss
          </button>
          <nav className="flex flex-col" aria-label="Mobile primary">
            {LINKS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                onClick={closeMenu}
                className={({ isActive }) => `${mobileRowClass}${isActive ? " font-medium" : ""}`}
                data-testid={item.testId}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
};

export default Navbar;
